import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { films } from "@/mocks/films";
import { categories } from "@/mocks/categories";

export default function FilmDetail() {
  const { id } = useParams<{ id: string }>();
  const film = films.find((item) => item.id === id);

  usePageMeta({
    title: film ? `${film.title} - 三级片电影详情介绍` : "影片未找到 - 三级片电影详情介绍",
    description: film
      ? `查看${film.title}的详细信息和影片简介，了解演员、剧情及相关三级片作品推荐。`
      : "该影片资料不存在或已下线，请返回分类页浏览其他三级片作品。",
    keywords: film
      ? `${film.title},${film.title}简介,三级片电影,三级片作品推荐`
      : "三级片,三级片电影",
  });

  if (!film) {
    return (
      <SiteLayout>
        <div className="rise-in rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
          <i className="ri-file-unknow-line text-4xl text-background-400" />
          <h1 className="mt-4 text-xl font-bold text-foreground-950">影片未找到</h1>
          <p className="mt-2 text-sm text-foreground-600">该影片资料可能已下线，或链接地址有误。</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category/drama"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回分类页
            </Link>
            <Link
              to="/"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              回到首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const category = categories.find((item) => item.key === film.categoryKey);
  const categoryName = category ? category.name : "剧情";

  const related = films
    .filter((item) => item.id !== film.id)
    .map((item) => ({
      film: item,
      shared: item.categories.filter((key) => film.categories.includes(key)).length,
    }))
    .filter((item) => item.shared > 0)
    .sort((a, b) => b.shared - a.shared || b.film.rating - a.film.rating)
    .slice(0, 4)
    .map((item) => item.film);

  const metaList = [
    { label: "导演", value: film.director, icon: "ri-user-line" },
    { label: "主演", value: film.cast.join(" / "), icon: "ri-team-line" },
    { label: "类型", value: `${categoryName}片 / 剧情长片`, icon: "ri-bookmark-3-line" },
    { label: "上映年份", value: `${film.year} 年`, icon: "ri-calendar-line" },
    { label: "地区", value: film.region, icon: "ri-map-pin-line" },
    { label: "片长", value: `${film.duration} 分钟`, icon: "ri-time-line" },
  ];

  return (
    <SiteLayout>
      <div className="rise-in">
        <Breadcrumb
          items={[
            { label: "影片分类", to: "/category/drama" },
            { label: `${categoryName}片`, to: `/category/${film.categoryKey}` },
            { label: film.title },
          ]}
        />

        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr] lg:gap-8">
          <div>
            <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <div className="aspect-[2/3] w-full">
                <img
                  src={film.cover}
                  alt={`${film.title} 影片海报`}
                  title={`${film.title} - 三级片电影详情介绍`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <span className="absolute left-3 top-3 rounded-md bg-primary-950/80 px-2 py-1 text-[11px] text-secondary-300">
                {film.region} · {film.year}
              </span>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">站内评分</span>
                <span className="flex items-baseline gap-1">
                  <span className="font-heading text-2xl font-black text-primary-700">
                    {film.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-400">/ 10</span>
                </span>
              </div>
              <div className="mt-2 flex gap-0.5 text-accent-500">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-xs" />
                ))}
              </div>
              <p className="mt-3 border-t border-background-200 pt-3 text-[11px] leading-relaxed text-foreground-500">
                评分与热度为站内虚构演示数据，不代表任何真实评价。
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-4">
              <p className="flex items-center gap-2 text-xs font-medium text-accent-800">
                <i className="ri-information-line" />
                观看说明
              </p>
              <p className="mt-2 text-[11px] leading-relaxed text-foreground-600">
                本站仅提供影片文字与图片资料，<strong className="font-medium text-foreground-800">不提供在线播放或下载</strong>。如需观看请通过正规渠道。
              </p>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {film.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900"
                >
                  {tag}
                </span>
              ))}
              <span className="rounded-full bg-primary-100 px-2.5 py-1 text-[11px] text-primary-800">
                {categoryName}片
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              {film.title}
            </h1>
            <p className="mt-1.5 text-sm text-foreground-500">{film.originalTitle}</p>

            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2 md:p-5">
              {metaList.map((meta) => (
                <div key={meta.label} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className={`${meta.icon} text-sm`} />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-[11px] text-foreground-400">{meta.label}</dt>
                    <dd className="mt-0.5 text-sm leading-relaxed text-foreground-800">{meta.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <section className="mt-7">
              <h2 className="flex items-center gap-2 border-l-2 border-accent-500 pl-2.5 text-lg font-bold text-foreground-950">
                影片简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-[15px]">{film.synopsis}</p>
            </section>

            <section className="mt-6">
              <h2 className="flex items-center gap-2 border-l-2 border-primary-500 pl-2.5 text-lg font-bold text-foreground-950">
                资料笔记
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-[15px]">{film.review}</p>
            </section>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/category/${film.categoryKey}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回{categoryName}分类
              </Link>
              <a
                href="#related"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-movie-line" />
                查看相关推荐
              </a>
            </div>
          </div>
        </div>

        <section id="related" className="mt-12 scroll-mt-24">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-accent-600">
                You may also like
              </p>
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
              <p className="mt-1.5 text-sm text-foreground-600">
                根据类型匹配，为你推荐相近题材的影片资料。
              </p>
            </div>
            <Link
              to={`/category/${film.categoryKey}`}
              className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-700"
            >
              更多{categoryName}片
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {related.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((item) => (
                <FilmCard key={item.id} film={item} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
              <p className="text-sm text-foreground-600">暂无相关推荐，敬请期待</p>
            </div>
          )}
        </section>
      </div>
    </SiteLayout>
  );
}